import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface SelectOption {
  value: string;
  label: string;
  hint?: string;       // small right-aligned secondary text (e.g. count, code)
  disabled?: boolean;
}

interface Props {
  label?: string;
  value: string;
  onChange: (v: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  searchable?: boolean;
  size?: 'sm' | 'md';
  className?: string;
  menuClassName?: string;
  'data-testid'?: string;
}

/**
 * Custom dark-theme dropdown matching the eagle-eye monitor pattern,
 * adapted for KAF Warehouse (slate-950 bg, sky accent).
 *
 * Why custom: native <select> drops the browser's white menu on dark
 * pages. This keeps the dark theme consistent end-to-end.
 */
export default function Select({
  label, value, onChange, options, placeholder = 'Select...',
  disabled = false, searchable = false, size = 'md', className, menuClassName,
  'data-testid': testId,
}: Props) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  useEffect(() => {
    if (open && searchable) {
      setQ('');
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open, searchable]);

  const filtered = q.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(q.trim().toLowerCase()))
    : options;

  const selected = options.find((o) => o.value === value);
  const triggerHeight = size === 'sm' ? 'h-9 text-xs' : 'h-10 text-sm';

  return (
    <div ref={rootRef} className={cn('relative', className)} data-testid={testId}>
      {label && (
        <label className="block text-xs text-muted mb-1.5">{label}</label>
      )}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'w-full flex items-center justify-between gap-2 rounded-lg border bg-bg px-3 transition focus:outline-none',
          triggerHeight,
          open
            ? 'border-accent-400 ring-1 ring-accent-400/40'
            : 'border-slate-800 hover:border-slate-700',
          disabled && 'opacity-50 cursor-not-allowed',
          !selected && 'text-muted'
        )}
      >
        <span className="truncate text-left text-muted-strong">
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown
          size={14}
          className={cn(
            'shrink-0 text-muted transition-transform duration-150',
            open && 'rotate-180 text-accent-400'
          )}
        />
      </button>

      {open && (
        <div
          className={cn(
            // z-[60] beats sticky bars (z-40) + everything except modal overlays.
            // bg-slate-950 (no transparency) so cards never bleed through.
            'absolute z-[60] mt-1.5 w-full min-w-[200px] rounded-xl border border-slate-700 bg-slate-950 p-1 shadow-2xl shadow-black/60',
            menuClassName
          )}
          style={{ animation: 'dropIn 0.15s ease' }}
        >
          {searchable && (
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search..."
              className="w-full mb-1 h-8 rounded-md bg-bg border border-slate-800 px-2 text-xs text-muted-strong focus:outline-none focus:border-accent-400"
            />
          )}
          <div className="max-h-72 overflow-y-auto">
            {filtered.length === 0 && (
              <div className="text-xs text-muted px-2.5 py-2">No matches</div>
            )}
            {filtered.map((o) => {
              const isActive = o.value === value;
              return (
                <button
                  key={o.value}
                  type="button"
                  disabled={o.disabled}
                  onClick={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                  className={cn(
                    'w-full flex items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm transition text-left',
                    isActive
                      ? 'bg-accent-600/15 text-accent-400'
                      : 'text-muted-strong hover:bg-slate-800/60',
                    o.disabled && 'opacity-50 cursor-not-allowed'
                  )}
                >
                  <span className="truncate flex items-center gap-2">
                    {isActive && <Check size={12} className="text-accent-400 shrink-0" />}
                    {o.label}
                  </span>
                  {o.hint && (
                    <span className="text-[10px] text-muted bg-slate-800/60 rounded-full px-2 py-0.5 shrink-0">
                      {o.hint}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// Render-time effect node that reads as content for animation
export function SelectContent({ children }: { children: ReactNode }) {
  return <>{children}</>;
}
